import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Save, Loader2, ExternalLink, Gauge, ArrowRight, AlertTriangle, Tags, Wand2 } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";

function AtsScoreRing({ score }) {
  const color = score >= 80 ? "#34d399" : score >= 60 ? "#FBBF24" : "#f87171";
  const circumference = 2 * Math.PI * 26;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative w-16 h-16 shrink-0" data-testid="ats-score-ring">
      <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
        <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
        <circle cx="32" cy="32" r="26" fill="none" stroke={color} strokeWidth="6" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-white">{score}</span>
      </div>
    </div>
  );
}

export default function AutoApplyPanel() {
  const [resumeText, setResumeText] = useState("");
  const [autoApplyEnabled, setAutoApplyEnabled] = useState(false);
  const [applications, setApplications] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [atsData, setAtsData] = useState(null);
  const [jdText, setJdText] = useState("");
  const [jdChecking, setJdChecking] = useState(false);
  const [jdResult, setJdResult] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/jobs/me/settings"),
      api.get("/jobs/me/applications"),
    ]).then(([settingsRes, appsRes]) => {
      setResumeText(settingsRes.data.resume_text || "");
      setAutoApplyEnabled(settingsRes.data.auto_apply_enabled || false);
      setApplications(appsRes.data || []);
      if (settingsRes.data.ats_score != null) {
        setAtsData({
          ats_score: settingsRes.data.ats_score, ats_overall_score: settingsRes.data.ats_overall_score,
          ats_top_improvements: settingsRes.data.ats_top_improvements || [], ats_checked_at: settingsRes.data.ats_checked_at,
        });
      }
    }).finally(() => setLoading(false));
  }, []);

  const saveResume = async () => {
    if (resumeText.trim().length < 50) {
      toast.error("Please add at least a few lines of your resume (50+ characters)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/jobs/me/resume", { resume_text: resumeText });
      setAtsData(data);
      toast.success(`Resume saved — ATS Score: ${data.ats_score}/100`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save resume");
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoApply = async (enabled) => {
    try {
      const { data } = await api.put("/jobs/me/auto-apply", { enabled });
      setAutoApplyEnabled(data.enabled);
      if (!data.enabled && enabled) toast.error(data.message);
      else toast.success(data.message);
    } catch (e) {
      toast.error("Could not update Auto-Apply setting");
    }
  };

  const checkAgainstJd = async () => {
    if (jdText.trim().length < 20) {
      toast.error("Paste a fuller job description (20+ characters)");
      return;
    }
    setJdChecking(true);
    setJdResult(null);
    try {
      const { data } = await api.post("/jobs/me/ats-check", { job_description: jdText });
      setJdResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check ATS + JD match right now");
    } finally {
      setJdChecking(false);
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-5 mb-8">
      <div className="glass-panel rounded-2xl p-5" data-testid="auto-apply-settings-panel">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-400" />
            <p className="text-sm font-semibold text-white">AI Auto-Apply</p>
          </div>
          <Switch checked={autoApplyEnabled} onCheckedChange={toggleAutoApply} data-testid="auto-apply-toggle" />
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Save your resume once — when enabled, AI evaluates every new job posting and drafts a cover note for any
          role matching 70%+, so you can apply in one click. The final application is always submitted by you.
        </p>
        <div className="flex items-center justify-end mb-2">
          <Link to="/resume-builder" data-testid="build-resume-open-button" className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200">
            <Wand2 className="w-3.5 h-3.5" /> Build My Resume with AI
          </Link>
        </div>
        <textarea
          value={resumeText}
          onChange={(e) => setResumeText(e.target.value)}
          placeholder="Paste your resume text here for AI job matching..."
          rows={4}
          data-testid="auto-apply-resume-input"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
        />
        <button
          onClick={saveResume}
          disabled={saving}
          data-testid="auto-apply-save-resume-button"
          className="mt-3 flex items-center gap-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save Resume
        </button>
      </div>

      {atsData && (
        <div className="glass-panel rounded-2xl p-5" data-testid="ats-score-tracker-panel">
          <div className="flex items-center gap-2 mb-3">
            <Gauge className="w-4 h-4 text-amber-400" />
            <p className="text-sm font-semibold text-white">ATS Score Tracker</p>
          </div>
          <div className="flex items-center gap-4">
            <AtsScoreRing score={atsData.ats_score} />
            <div className="flex-1">
              <p className="text-xs text-slate-400">
                <span className="text-white font-semibold">{atsData.ats_overall_score}</span> overall resume score · how well an ATS would parse & rank this resume
              </p>
              {atsData.ats_top_improvements?.length > 0 && (
                <p className="text-xs text-slate-400 mt-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" /> {atsData.ats_top_improvements[0]}
                </p>
              )}
            </div>
          </div>
          <Link to="/resume-builder" data-testid="ats-score-view-full-analysis-link" className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200">
            View Full AI Resume Analysis <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}

      {resumeText.trim().length >= 50 && (
        <div className="glass-panel rounded-2xl p-5" data-testid="jd-match-check-panel">
          <div className="flex items-center gap-2 mb-3">
            <Tags className="w-4 h-4 text-emerald-400" />
            <p className="text-sm font-semibold text-white">Check ATS + JD Match for a Specific Job</p>
          </div>
          <textarea
            value={jdText}
            onChange={(e) => setJdText(e.target.value)}
            placeholder="Paste a job description here to check your ATS score + JD match against it..."
            rows={3}
            data-testid="jd-match-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white resize-none focus:outline-none focus:ring-1 focus:ring-emerald-400"
          />
          <button
            onClick={checkAgainstJd}
            disabled={jdChecking}
            data-testid="jd-match-check-button"
            className="mt-3 flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            {jdChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Check ATS + JD Match
          </button>

          {jdResult && (
            <div className="mt-4 pt-4 border-t border-white/10" data-testid="jd-match-result">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-heading font-bold text-indigo-400" data-testid="jd-match-ats-score">{jdResult.ats_score}</p>
                  <p className="text-[11px] text-slate-400">ATS Score</p>
                </div>
                {jdResult.jd_match_score !== undefined && (
                  <div className="text-center">
                    <p className="text-2xl font-heading font-bold text-emerald-400" data-testid="jd-match-score">{jdResult.jd_match_score}</p>
                    <p className="text-[11px] text-slate-400">JD Match</p>
                  </div>
                )}
              </div>
              {jdResult.missing_keywords?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-slate-300 mb-1.5">Missing Keywords</p>
                  <div className="flex flex-wrap gap-1.5">
                    {jdResult.missing_keywords.map((k, i) => (
                      <span key={i} className="text-[11px] bg-red-400/10 border border-red-400/20 text-red-300 rounded-full px-2.5 py-1">{k}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {applications.length > 0 && (
        <div data-testid="ai-matched-applications">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-indigo-400 mb-3">AI-Matched For You</p>
          <div className="space-y-3">
            {applications.map((a) => (
              <div key={a.id} className="glass-panel rounded-xl p-4" data-testid={`ai-application-${a.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-white">{a.job_title} <span className="text-indigo-400">· {a.company}</span></p>
                    <p className="text-xs text-emerald-400 font-semibold mt-1">{a.match_score}% match</p>
                    <p className="text-xs text-slate-400 mt-2 leading-relaxed">{a.cover_note}</p>
                  </div>
                  <a
                    href={a.apply_url} target="_blank" rel="noopener noreferrer" data-testid={`ai-application-apply-btn-${a.id}`}
                    className="shrink-0 flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
                  >
                    Apply <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
